import { API_URL } from '../api';

// Browser + Capacitor geolocation helpers, distance math, and formatting.
//
// All math runs client-side; the user's coordinates never leave the
// browser. Distance is computed against properties whose latitude /
// longitude are already in the loaded payload from /properties, so no
// extra server round-trip is needed when sorting "nearest first".

// Haversine — great-circle distance between two lat/lng pairs in km.
// Same formula used server-side in routes/sync.js for SP ranking; kept
// duplicated here to keep the client bundle small (no shared util cost).
export function haversineKm(lat1, lng1, lat2, lng2) {
  if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return Infinity;
  const R = 6371; // earth radius in km
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Pretty-print a distance in km. Under 1km → metres ("450 m"); otherwise
// up to 1 decimal place ("2.4 km"). Returns null on Infinity / NaN /
// missing input so callers can `&&`-render without extra guards.
export function formatDistance(km) {
  if (km == null || !Number.isFinite(km)) return null;
  if (km < 1) return `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
}

// Capacitor detection — we have to check at runtime because the SAME
// React bundle runs in both web and the Android/iOS app. When inside a
// Capacitor WebView, the native plugin gives a much better permission UX
// (system dialog, persistent grant) than the browser API.
const _isCapacitor = typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();

/**
 * Request the user's current location. Resolves with { lat, lng, accuracy }
 * on success. Rejects with { code, message } on permission denial,
 * timeout, or unsupported platform — callers can show a graceful error
 * without surfacing the raw browser DOMException.
 *
 * No auto-retry, no watching — this is a one-shot fetch suitable for
 * "Use my location" button clicks. For live tracking, use watchPosition
 * from the underlying API (we don't need it for property search).
 *
 * @param {object} [opts]
 * @param {boolean} [opts.highAccuracy=true]  Use GPS on mobile (slower but precise).
 * @param {number}  [opts.timeoutMs=10000]    Reject after this long.
 * @returns {Promise<{lat: number, lng: number, accuracy: number}>}
 */
export async function getCurrentLocation(opts = {}) {
  const { highAccuracy = true, timeoutMs = 10000 } = opts;

  // Capacitor native path — better permission UX on mobile. Only kicks
  // in if @capacitor/geolocation is installed AND we're on a native
  // platform. We `require` it inside the function so a missing plugin
  // doesn't blow up at module load time.
  if (_isCapacitor) {
    try {
      const { Geolocation } = await import('@capacitor/geolocation');
      const perm = await Geolocation.checkPermissions();
      if (perm.location !== 'granted') {
        const req = await Geolocation.requestPermissions();
        if (req.location !== 'granted') {
          const err = new Error('Location permission denied');
          err.code = 'permission_denied';
          throw err;
        }
      }
      const pos = await Geolocation.getCurrentPosition({
        enableHighAccuracy: highAccuracy,
        timeout: timeoutMs,
      });
      return {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      };
    } catch (e) {
      // Fall through to the web API if the plugin isn't actually
      // installed in this build (e.g. WebView app that hasn't been
      // updated yet). The web API works in Capacitor WebView too,
      // just with worse permission UX.
      // Only a real user denial should bubble — webpack's stub for the
      // missing module rejects with e.code = 'MODULE_NOT_FOUND', which
      // the old `if (e.code)` check re-threw, so the fallback below
      // never ran on Android.
      if (e && e.code === 'permission_denied') throw e;
    }
  }

  // Web API path (also the fallback for Capacitor when plugin missing).
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    const err = new Error("This device doesn't support geolocation");
    err.code = 'unsupported';
    throw err;
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      }),
      (err) => {
        // Map the standard PositionError codes to a real Error object
        // with .code attached so callers can branch on it (UI shows
        // different guidance for permission_denied vs timeout vs no-GPS).
        const map = {
          1: { code: 'permission_denied', message: 'Location access is blocked. Allow it from your browser settings, or search a place instead.' },
          2: { code: 'position_unavailable', message: 'Could not determine your location. Check GPS / network, or search a place instead.' },
          3: { code: 'timeout', message: 'Location request timed out. Try again with stronger signal, or search a place instead.' },
        };
        const info = map[err.code] || { code: 'unknown', message: err.message || 'Unknown geolocation error' };
        const errObj = new Error(info.message);
        errObj.code = info.code;
        reject(errObj);
      },
      {
        enableHighAccuracy: highAccuracy,
        timeout: timeoutMs,
        maximumAge: 60_000, // accept a cached fix up to 1 minute old
      }
    );
  });
}

/**
 * Enrich a property list with `_distance_km` relative to a reference point.
 * Properties without lat/lng get Infinity so they naturally sort last.
 * Returns a NEW array — input is not mutated.
 */
export function withDistances(properties, ref) {
  if (!ref || ref.lat == null || ref.lng == null) return properties;
  return properties.map((p) => ({
    ...p,
    _distance_km: haversineKm(ref.lat, ref.lng, p.latitude, p.longitude),
  }));
}

/**
 * Sort properties by distance (closest first). Properties without coords
 * (Infinity distance) fall to the bottom. Stable for ties — preserves
 * input order, so distance-tied items keep their relevance/recency rank.
 */
export function sortByDistance(properties) {
  return [...properties].sort((a, b) => {
    const da = a._distance_km == null ? Infinity : a._distance_km;
    const db = b._distance_km == null ? Infinity : b._distance_km;
    return da - db;
  });
}

/**
 * Filter to properties within `maxKm` of the reference. Properties
 * without coords are excluded when a radius is active — they can't be
 * proven to be inside the radius. `null` / `undefined` maxKm = no
 * filter, return input as-is.
 */
export function filterByRadius(properties, maxKm) {
  if (maxKm == null || !Number.isFinite(maxKm)) return properties;
  return properties.filter((p) => p._distance_km != null && p._distance_km <= maxKm);
}

// ── Place search via OUR server (which proxies to Nominatim) ───────
//
// Calling Nominatim direct from the browser fails too often in
// practice:
//   - Ad blockers / privacy extensions target nominatim.openstreetmap.org
//   - Browsers can't set the User-Agent header Nominatim's WAF prefers
//   - Some hosting environments fail CORS preflight in subtle ways
//
// Routing through our own /api/places/search endpoint sidesteps all
// three. The server handles UA + rate-limiting + (potential future)
// caching; the browser only does same-origin fetches.

const _geocodeCache = new Map(); // q → results[]  (per session)

/**
 * Search places matching the user's text query. Returns up to N
 * suggestions, each shaped { lat, lng, display_name, place_id }.
 *
 * Hits OUR server at /api/places/search — see server/routes/places.js
 * for the implementation. Cached per-session in-memory so repeating
 * the same query is free.
 *
 * @param {string} query   Free-text place / address
 * @param {object} [opts]
 * @param {number} [opts.limit=5]
 * @param {AbortSignal} [opts.signal]       For cancellation on input change
 * @returns {Promise<Array<{lat: number, lng: number, display_name: string, place_id: string|number}>>}
 */
export async function searchPlaces(query, opts = {}) {
  const q = String(query || '').trim();
  if (!q || q.length < 2) return [];

  const { limit = 5, signal } = opts;
  const cacheKey = `${q.toLowerCase()}|${limit}`;
  if (_geocodeCache.has(cacheKey)) return _geocodeCache.get(cacheKey);

  const params = new URLSearchParams({ q, limit: String(limit) });

  try {
    const res = await fetch(`${API_URL}/places/search?${params.toString()}`, {
      signal,
      headers: { 'Accept': 'application/json' },
    });
    if (!res.ok) {
      // 429 from rate limit, 5xx from upstream — return empty so the
      // dropdown shows "no matches" rather than crashing. Caller can
      // also surface a less-aggressive message based on status.
      return [];
    }
    const body = await res.json();
    const results = Array.isArray(body?.results) ? body.results : [];
    _geocodeCache.set(cacheKey, results);
    return results;
  } catch (e) {
    // Abort errors are expected (user typed faster than the network);
    // re-throw so the React effect can ignore the stale request and
    // wait for the latest one. Anything else, return empty so the UI
    // shows "no results" instead of crashing.
    if (e?.name === 'AbortError') throw e;
    return [];
  }
}

/**
 * Pretty-print a Nominatim result for the dropdown — short primary
 * line (e.g. "Sivakasi") + secondary (state, country) on a second
 * line. Nominatim's `display_name` is comma-separated. We split,
 * keep the first segment as title, join the rest as subtitle.
 */
export function summarizePlace(place) {
  if (!place?.display_name) return { title: '', subtitle: '' };
  const parts = place.display_name.split(',').map(s => s.trim()).filter(Boolean);
  return {
    title: parts[0] || '',
    subtitle: parts.slice(1, 4).join(', '), // first 3 levels of admin hierarchy
  };
}

// ── Recent searches — persisted across sessions ──────────────────
//
// localStorage-backed FIFO list (max 5). Stores the user's most
// recent place picks so the search dropdown can show one-tap shortcuts
// to common destinations ("Sivakasi", "Madurai", "Chennai T Nagar")
// without having to retype every time.
//
// Storage key is per-app (no per-user namespacing) since this is the
// public marketplace where the user may or may not be signed in. The
// data is non-sensitive (just place names + coords).

const RECENT_KEY = 'recentPlaceSearches';
const RECENT_MAX = 5;

/**
 * Read the recent-searches list from localStorage. Returns at most
 * RECENT_MAX entries, oldest dropped first.
 * @returns {Array<{lat: number, lng: number, display_name: string, place_id: any}>}
 */
export function getRecentPlaces() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(arr)) return [];
    // Defensive — strip anything that doesn't look like a real place.
    return arr.filter(p => p && Number.isFinite(p.lat) && Number.isFinite(p.lng) && p.display_name);
  } catch {
    return [];
  }
}

/**
 * Add a place to the recent-searches list. De-duplicates by place_id
 * (or by display_name as fallback for older entries without id). The
 * most-recently-picked place floats to the top; entries past
 * RECENT_MAX get dropped.
 */
export function addRecentPlace(place) {
  if (!place || !Number.isFinite(place.lat) || !Number.isFinite(place.lng)) return;
  try {
    const existing = getRecentPlaces();
    const dedupKey = place.place_id != null ? String(place.place_id) : (place.display_name || '');
    const filtered = existing.filter(p => {
      const k = p.place_id != null ? String(p.place_id) : (p.display_name || '');
      return k !== dedupKey;
    });
    const next = [
      {
        lat: place.lat,
        lng: place.lng,
        display_name: place.display_name,
        place_id: place.place_id,
      },
      ...filtered,
    ].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch (_) { /* localStorage full or disabled — silently skip */ }
}

/** Clear all recent searches. */
export function clearRecentPlaces() {
  try { localStorage.removeItem(RECENT_KEY); } catch (_) {}
}
